export type BrandSchemeName = "light" | "dark";


export const BRAND_SCHEME_AUTO = 0;
export const BRAND_SCHEME_LIGHT = 1;
export const BRAND_SCHEME_DARK = 2;

export interface Rgb {
    r: number;
    g: number;
    b: number;
}


export type BrandThemeTokens = Record<string, string>;


export const DEFAULT_BRAND_THEME: Readonly<BrandThemeTokens> = Object.freeze({
    "--vlp-sidebar-bg-top": "#0c1b35",
    "--vlp-sidebar-bg-mid": "#0b1730",
    "--vlp-sidebar-bg-bottom": "#081326",
    "--vlp-sidebar-fg": "#cbd5e1",
    "--vlp-sidebar-fg-strong": "#ffffff",
    "--vlp-sidebar-fg-muted": "#7f91ad",
    "--vlp-sidebar-accent": "#2dd4bf",
    "--vlp-sidebar-accent-strong": "#5eead4",
    "--vlp-sidebar-accent-soft": "rgba(45,212,191,0.13)",
    "--vlp-sidebar-accent-faint": "rgba(45,212,191,0.07)",
    "--vlp-sidebar-accent-ring": "rgba(45,212,191,0.55)",
    "--vlp-sidebar-active-from": "rgba(20,184,166,0.2)",
    "--vlp-sidebar-active-to": "rgba(20,184,166,0.07)",
    "--vlp-sidebar-border": "rgba(255,255,255,0.08)",
    "--vlp-sidebar-hover": "rgba(255,255,255,0.065)",
    "--vlp-sidebar-surface": "rgba(255,255,255,0.055)",
    "--vlp-sidebar-overlay-strong": "rgba(255,255,255,0.14)",
    "--vlp-sidebar-overlay-faint": "rgba(255,255,255,0.035)",
    "--vlp-sidebar-input-bg": "rgba(5,16,34,0.42)",
    "--vlp-sidebar-glow": "rgba(20,184,166,0.16)",
    "--vlp-brand-primary": "#2dd4bf",
});

const NEAR_BLACK: Rgb = { r: 15, g: 23, b: 42 };
const NEAR_WHITE: Rgb = { r: 248, g: 250, b: 252 };
const DARK_FLOOR: Rgb = { r: 6, g: 13, b: 28 };
const WHITE: Rgb = { r: 255, g: 255, b: 255 };


const CONTRAST_TEXT = 4.5;
const CONTRAST_SUPPORTING = 3;

export function parseHexColor(value?: string | null): Rgb | null {
    const raw = (value ?? "").trim();
    if (!/^#[0-9a-fA-F]{6}$/.test(raw)) {
        return null;
    }
    return {
        r: parseInt(raw.slice(1, 3), 16),
        g: parseInt(raw.slice(3, 5), 16),
        b: parseInt(raw.slice(5, 7), 16),
    };
}

export function toHexColor(color: Rgb): string {
    const part = (value: number): string => clampByte(value).toString(16).padStart(2, "0");
    return `#${part(color.r)}${part(color.g)}${part(color.b)}`;
}


export function relativeLuminance(color: Rgb): number {
    const channel = (value: number): number => {
        const v = clampByte(value) / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}


export function mixColor(from: Rgb, to: Rgb, amount: number): Rgb {
    const t = Math.min(1, Math.max(0, amount));
    return {
        r: Math.round(from.r + (to.r - from.r) * t),
        g: Math.round(from.g + (to.g - from.g) * t),
        b: Math.round(from.b + (to.b - from.b) * t),
    };
}


export function ensureContrast(color: Rgb, background: Rgb, minimum: number): Rgb {
    if (contrastRatio(color, background) >= minimum) {
        return color;
    }

    const target = relativeLuminance(background) > 0.45 ? NEAR_BLACK : NEAR_WHITE;
    for (let step = 1; step <= 20; step++) {
        const candidate = mixColor(color, target, step / 20);
        if (contrastRatio(candidate, background) >= minimum) {
            return candidate;
        }
    }
    return target;
}


export function readableForeground(background: Rgb): Rgb {
    return contrastRatio(NEAR_WHITE, background) >= contrastRatio(NEAR_BLACK, background)
        ? NEAR_WHITE
        : NEAR_BLACK;
}


export function resolveScheme(primary: Rgb | null, scheme: number): BrandSchemeName {
    if (scheme === BRAND_SCHEME_LIGHT) {
        return "light";
    }
    if (scheme === BRAND_SCHEME_DARK) {
        return "dark";
    }
    if (!primary) {
        return "dark";
    }
    return relativeLuminance(primary) > 0.35 ? "light" : "dark";
}

export interface BrandThemeInput {
    
    primaryColor?: string | null;
    
    sidebarScheme?: number | null;
}


export function buildBrandTheme(input: BrandThemeInput): BrandThemeTokens {
    const primary = parseHexColor(input.primaryColor);
    const scheme = resolveScheme(primary, Number(input.sidebarScheme) || BRAND_SCHEME_AUTO);

    if (!primary) {
        return { ...DEFAULT_BRAND_THEME };
    }

    const [top, mid, bottom] = scheme === "dark"
        ? [mixColor(primary, DARK_FLOOR, 0.76), mixColor(primary, DARK_FLOOR, 0.82), mixColor(primary, DARK_FLOOR, 0.88)]
        : [mixColor(primary, WHITE, 0.9), mixColor(primary, WHITE, 0.94), mixColor(primary, WHITE, 0.97)];
    const reference = relativeLuminance(top) > relativeLuminance(bottom) ? top : bottom;
    const strong = readableForeground(reference);
    const body = ensureContrast(mixColor(strong, reference, 0.18), reference, CONTRAST_TEXT);
    const muted = ensureContrast(mixColor(strong, reference, 0.45), reference, CONTRAST_SUPPORTING);
    const accent = ensureContrast(primary, reference, CONTRAST_SUPPORTING);
    const overlay = strong === NEAR_WHITE ? "255,255,255" : "15,23,42";

    return {
        "--vlp-sidebar-bg-top": toHexColor(top),
        "--vlp-sidebar-bg-mid": toHexColor(mid),
        "--vlp-sidebar-bg-bottom": toHexColor(bottom),
        "--vlp-sidebar-fg": toHexColor(body),
        "--vlp-sidebar-fg-strong": toHexColor(strong),
        "--vlp-sidebar-fg-muted": toHexColor(muted),
        "--vlp-sidebar-accent": toHexColor(accent),
        "--vlp-sidebar-accent-strong": toHexColor(ensureContrast(accent, reference, CONTRAST_TEXT)),
        "--vlp-sidebar-accent-soft": rgba(accent, 0.13),
        "--vlp-sidebar-accent-faint": rgba(accent, 0.07),
        "--vlp-sidebar-accent-ring": rgba(accent, 0.55),
        "--vlp-sidebar-active-from": rgba(accent, 0.2),
        "--vlp-sidebar-active-to": rgba(accent, 0.07),
        "--vlp-sidebar-border": `rgba(${overlay},0.12)`,
        "--vlp-sidebar-hover": `rgba(${overlay},0.09)`,
        "--vlp-sidebar-surface": `rgba(${overlay},0.07)`,
        "--vlp-sidebar-overlay-strong": `rgba(${overlay},0.16)`,
        "--vlp-sidebar-overlay-faint": `rgba(${overlay},0.04)`,
        "--vlp-sidebar-input-bg": scheme === "dark" ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.75)",
        "--vlp-sidebar-glow": `rgba(${primary.r},${primary.g},${primary.b},0.18)`,
        "--vlp-brand-primary": toHexColor(primary),
    };
}


function rgba(color: Rgb, alpha: number): string {
    return `rgba(${color.r},${color.g},${color.b},${alpha})`;
}

function clampByte(value: number): number {
    return Math.min(255, Math.max(0, Math.round(value)));
}
